import React, { useEffect, useState } from "react";
import moment from "moment";
import DataTable, { defaultThemes } from "react-data-table-component";
import Loader from "react-loader-spinner";
import { ApiGet, ApiPatch } from "../../../helpers/API/ApiData";
import { toast } from "react-toastify";

const customStyles = {
  header: {
    style: {
      minHeight: "56px",
    },
  },
  headRow: {
    style: {
      borderTopStyle: "solid",
      borderTopWidth: "1px",
      borderTopColor: defaultThemes.default.divider.default,
    },
  },
  headCells: {
    style: {
      "&:not(:last-of-type)": {
        borderRightStyle: "solid",
        borderRightWidth: "1px",
        borderRightColor: defaultThemes.default.divider.default,
      },
    },
  },
  cells: {
    style: {
      "&:not(:last-of-type)": {
        borderRightStyle: "solid",
        borderRightWidth: "1px",
        borderRightColor: defaultThemes.default.divider.default,
      },
    },
  },
};

const BillingManagement = () => {
  const [plans, setPlans] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [loadingRequests, setLoadingRequests] = useState(false);

  const loadPlans = async () => {
    setLoadingPlans(true);
    try {
      const { data } = await ApiGet("admin/billing/plans");
      setPlans(data.payload.plans || []);
    } catch (error) {
      toast.error("Unable to load plans");
    } finally {
      setLoadingPlans(false);
    }
  };

  const loadRequests = async () => {
    setLoadingRequests(true);
    try {
      const { data } = await ApiGet("admin/billing/verification");
      setRequests(data.payload.requests || []);
    } catch (error) {
      toast.error("Unable to load verification requests");
    } finally {
      setLoadingRequests(false);
    }
  };

  useEffect(() => {
    loadPlans();
    loadRequests();
  }, []);

  const handleVerificationAction = async (id, action) => {
    try {
      const payload =
        action === "reject"
          ? { reason: window.prompt("Reason for rejection?"), adminNote: "Rejected via admin panel" }
          : { adminNote: "Approved via admin panel" };
      if (action === "reject" && !payload.reason) {
        toast.warning("Reject reason is required");
        return;
      }
      await ApiPatch(`admin/billing/verification/${id}/${action}`, payload);
      toast.success(`Verification ${action}ed`);
      loadRequests();
    } catch (error) {
      toast.error(error?.message || "Unable to update verification");
    }
  };

  const togglePlanStatus = async (plan) => {
    const nextStatus = plan.status === "active" ? "disabled" : "active";
    try {
      await ApiPatch(`admin/billing/plans/${plan._id}/status`, { status: nextStatus });
      toast.success(`Plan ${plan.name} is now ${nextStatus}`);
      loadPlans();
    } catch (error) {
      toast.error(error?.message || "Unable to update plan status");
    }
  };

  const formatCurrency = (plan) => {
    const priceCents = plan.priceCents ?? 0;
    const currency = (plan.currency || "usd").toUpperCase();
    return `${(priceCents / 100).toFixed(2)} ${currency}`;
  };

  const planColumns = [
    {
      name: "S.No.",
      cell: (_, index) => index + 1,
      width: "70px",
    },
    {
      name: "Plan name",
      selector: "name",
      sortable: true,
    },
    {
      name: "Interval",
      selector: "billingInterval",
      sortable: true,
    },
    {
      name: "Price",
      cell: (row) => formatCurrency(row),
      sortable: true,
    },
    {
      name: "Status",
      selector: "status",
      sortable: true,
    },
    {
      name: "Created",
      cell: (row) => (
        <div>{row.createdAt ? moment(row.createdAt).utc().format("MMM Do YYYY, h:mm a") : "-"}</div>
      ),
      sortable: true,
    },
    {
      name: "Actions",
      cell: (row) => {
        const label = row.status === "active" ? "Disable" : "Enable";
        return (
          <button
            type="button"
            onClick={() => togglePlanStatus(row)}
            style={{
              border: "none",
              padding: "6px 12px",
              borderRadius: 6,
              backgroundColor: "#0f4c81",
              color: "#fff",
              cursor: "pointer",
            }}
          >
            {label}
          </button>
        );
      },
    },
  ];

  const requestColumns = [
    {
      name: "S.No.",
      cell: (_, index) => index + 1,
      width: "70px",
    },
    {
      name: "User Id",
      selector: "userId",
      wrap: true,
    },
    {
      name: "Plan Id",
      selector: "planId",
      wrap: true,
    },
    {
      name: "Status",
      selector: "status",
      sortable: true,
    },
    {
      name: "Submitted",
      cell: (row) => (
        <div>
          {row.submittedAt ? moment(row.submittedAt).utc().format("MMM Do YYYY, h:mm a") : "-"}
        </div>
      ),
      sortable: true,
    },
    {
      name: "Actions",
      cell: (row) => {
        if (row.status !== "pending") {
          return <span style={{ color: "#818181" }}>No actions</span>;
        }
        return (
          <div style={{ display: "flex", gap: 8 }}>
            <button
              type="button"
              onClick={() => handleVerificationAction(row._id, "approve")}
              style={{
                border: "none",
                padding: "6px 12px",
                borderRadius: 6,
                backgroundColor: "#27ae60",
                color: "#fff",
                cursor: "pointer",
              }}
            >
              Approve
            </button>
            <button
              type="button"
              onClick={() => handleVerificationAction(row._id, "reject")}
              style={{
                border: "none",
                padding: "6px 12px",
                borderRadius: 6,
                backgroundColor: "#c0392b",
                color: "#fff",
                cursor: "pointer",
              }}
            >
              Reject
            </button>
          </div>
        );
      },
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-end",
          marginBottom: 20,
        }}
      >
        <div>
          <h2 style={{ margin: 0 }}>Billing Control</h2>
         
        </div>
        <div style={{ color: "#6b6b6b" }}>{moment().format("MMM Do YYYY")}</div>
      </div>

      <section style={{ marginBottom: 36 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 12,
          }}
        >
          <h3 style={{ margin: 0 }}>Plans ({plans.length})</h3>
          <span style={{ color: "#8a8a8a" }}>Last refreshed {moment().fromNow()}</span>
        </div>
        <DataTable
          columns={planColumns}
          data={plans}
          pagination
          customStyles={customStyles}
          progressPending={loadingPlans}
          progressComponent={<Loader type="Oval" color="#334D52" height={32} width={32} />}
        />
      </section>

      {/* <section>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 12,
          }}
        >
          <h3 style={{ margin: 0 }}>Verification Requests ({requests.length})</h3>
          <span style={{ color: "#8a8a8a" }}>Pending checks {requests.filter((r) => r.status === "pending").length}</span>
        </div>
        <DataTable
          columns={requestColumns}
          data={requests}
          pagination
          customStyles={customStyles}
          progressPending={loadingRequests}
          progressComponent={<Loader type="Oval" color="#334D52" height={32} width={32} />}
        />
      </section> */}
    </div>
  );
};

export default BillingManagement;
